import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Calculator, Fingerprint, Wallet } from 'lucide-react'
import { useState } from 'react'

import { tellerSessionQueryOptions } from '@/api/teller-api'
import { useAuth } from '@/auth/use-auth'
import { AuditTrailDrawer } from '@/components/audit-trail-drawer'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { CashCountDialog } from '@/features/teller/cash-count-dialog'
import { TELLER_PATHS, useTellerNavigate, useTellerParams, useTellerRawSearch } from '@/features/teller/navigation'
import { TELLER_PERMISSIONS, useTellerPermissions } from '@/features/teller/permissions'
import { CloseSessionDialog } from '@/features/teller/session-actions'
import {
  CashCountPanel,
  PermissionNotice,
  SessionFactsPanel,
  SessionFiguresPanel,
} from '@/features/teller/teller-panels'
import { TellerLink } from '@/features/teller/teller-link'

import { tellerSessionDetailSearchSchema, type SessionDetailTab } from './search'

const PANEL_ID = 'teller-session-panel'

/**
 * One shift: its arithmetic, its counts, and its close.
 *
 * It refuses below `lg`. Reading a shift would be harmless on a phone, but the two things this
 * screen exists for — counting a drawer denomination by denomination, and declaring what is in it —
 * are the keyed-amount case docs/mobile-policy.md draws the line at, and a screen that showed the
 * figures while withholding the actions it is named for would be a worse answer than asking for a
 * desk.
 */
export function TellerSessionDetailPage() {
  const params = useTellerParams()
  const rawSearch = useTellerRawSearch()
  const navigate = useTellerNavigate()
  const permissions = useTellerPermissions()
  const { user } = useAuth()

  const sessionId = params.sessionId ?? ''
  const search = tellerSessionDetailSearchSchema.parse(rawSearch)
  const tab: SessionDetailTab = search.tab ?? 'shift'

  const [counting, setCounting] = useState(false)
  const [closing, setClosing] = useState(false)
  const [auditOpen, setAuditOpen] = useState(false)

  const query = useQuery({
    ...tellerSessionQueryOptions(sessionId),
    enabled: permissions.canViewTills && sessionId !== '',
  })

  const selectTab = (next: SessionDetailTab) => {
    navigate(TELLER_PATHS.session(sessionId), { search: { tab: next }, replace: true })
  }

  if (!permissions.canViewTills) {
    return (
      <>
        <PageHeader title="Shift" description="One shift, its counts, and its expected closing cash." />
        <PermissionNotice permission={TELLER_PERMISSIONS.tillView} what="This shift" />
      </>
    )
  }

  return (
    <RequiresWiderScreen
      screen="A teller shift"
      reason="Counting a drawer and declaring what is in it are keyed amounts against real money, and a mis-key here is found at end-of-day reconciliation."
    >
      <TellerLink
        to={TELLER_PATHS.sessions}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All shifts
      </TellerLink>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-9 w-full max-w-md" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This shift could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={`Till ${query.data.tillNumber}`}
            reference={query.data.tellerUsername}
            description="One till, one named person, one business day."
            badges={<StatusBadge status={query.data.status} />}
            actions={
              <>
                {/*
                  `TellerSession.RecordCount` refuses both terminal states — Closed *and*
                  ForcedClose — with `teller.session_not_open`. PendingClose is the one that stays
                  open to a count, deliberately: a rejected declaration is answered by counting
                  again.
                */}
                {permissions.canRecordCount &&
                query.data.status !== 'Closed' &&
                query.data.status !== 'ForcedClose' ? (
                  <Button variant="secondary" onClick={() => setCounting(true)}>
                    <Calculator />
                    Count the drawer
                  </Button>
                ) : null}
                {/*
                  Offered while the close is pending as well as while the drawer is open.
                  `TellerSession.Declare` accepts PendingClose on purpose — a fresh declaration
                  supersedes an unresolved one — and a teller whose count was rejected has no other
                  way to close and go home.
                */}
                {permissions.canSubmitEod &&
                query.data.tellerUserId === user?.id &&
                (query.data.status === 'Open' || query.data.status === 'PendingClose') ? (
                  <Button variant="primary" onClick={() => setClosing(true)}>
                    <Wallet />
                    Declare and close
                  </Button>
                ) : null}
                <Button variant="ghost" onClick={() => setAuditOpen(true)}>
                  <Fingerprint />
                  Audit trail
                </Button>
              </>
            }
          />

          {query.data.status === 'PendingClose' ? (
            <Alert tone="warning">
              <div className="space-y-1">
                <AlertTitle>Declared, and waiting for a countersignature</AlertTitle>
                <AlertDescription>
                  The drawer has stopped trading and the declaration is recorded, but the shift is not
                  closed and no over-or-short has posted. If the count is rejected, a fresh count
                  supersedes this declaration —{' '}
                  <span className="font-mono text-xs">teller.session_pending_close</span> is the
                  server saying exactly that.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          {query.data.tellerUserId !== user?.id ? (
            <Alert tone="info">
              <div className="space-y-1">
                <AlertTitle>This drawer is not yours</AlertTitle>
                <AlertDescription>
                  A drawer belongs to one person. You can read this shift and, holding{' '}
                  <span className="font-mono text-xs">{TELLER_PERMISSIONS.denominationRecord}</span>,
                  take a spot count of it — but only {query.data.tellerUsername} can record its
                  closing count or declare it (<span className="font-mono text-xs">teller.session_not_yours</span>).
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          <div className="space-y-4">
            <TabStrip<SessionDetailTab>
              tabs={
                [
                  { id: 'shift', label: 'The shift' },
                  {
                    id: 'count',
                    label: 'Latest count',
                    badge: query.data.latestCount ? undefined : '—',
                  },
                ] satisfies TabDefinition<SessionDetailTab>[]
              }
              active={tab}
              onChange={selectTab}
              panelId={PANEL_ID}
              label="Shift sections"
            />

            <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
              {tab === 'shift' ? (
                <div className="space-y-4">
                  <SessionFiguresPanel session={query.data} />
                  <SessionFactsPanel session={query.data} />
                </div>
              ) : query.data.latestCount === null ? (
                <Alert tone="info">
                  <div className="space-y-1">
                    <AlertTitle>This drawer has not been counted</AlertTitle>
                    <AlertDescription>
                      No count of any purpose has been recorded against this shift. That is a fact
                      about the shift, not a gap in this screen — and a closing declaration that has
                      no count behind it is exactly what a supervisor is looking for here.
                    </AlertDescription>
                  </div>
                </Alert>
              ) : (
                <CashCountPanel
                  count={query.data.latestCount}
                  expectedMinor={query.data.expectedClosingCashMinor}
                />
              )}
            </div>
          </div>

          <CashCountDialog
            session={query.data}
            open={counting}
            onOpenChange={setCounting}
            defaultPurpose={query.data.tellerUserId === user?.id ? 'Closing' : 'Spot'}
          />

          <CloseSessionDialog session={query.data} open={closing} onOpenChange={setClosing} />

          {/*
            The shift's own trail: it was opened, it was declared, it was closed. A cash count is
            deliberately not in it — the server records a count against its own `CashCount` entity
            with the count's identifier, so it will never appear under `TellerSession`. The count
            itself is on the tab beside this button, which is the honest place for it.
          */}
          <AuditTrailDrawer
            open={auditOpen}
            onOpenChange={setAuditOpen}
            entityType="TellerSession"
            entityId={query.data.id}
            subject={`Till ${query.data.tillNumber} · ${query.data.tellerUsername}`}
          />
        </>
      )}
    </RequiresWiderScreen>
  )
}
